import { Show, createMemo } from 'solid-js';
import { setFitTarget, clearSlot, replaceSlotImage } from '../state/actions';
import { setSelectedId, selectedId, pushToast } from './uiState';
import { checkDpi } from '../core/dpi';
import { SlotRowExpanded } from './SlotRowExpanded';
import type { Slot } from '../types';

export function SlotListItem(props: { slot: Slot }) {
  let inputRef!: HTMLInputElement;

  const dpi = createMemo(() => (props.slot.image ? checkDpi(props.slot.image, props.slot.fitTarget) : null));
  const dimsLabel = () => {
    const img = props.slot.image;
    if (!img) return 'empty';
    if (img.kind === 'vector') return 'vector';
    return `${img.naturalWidth}×${img.naturalHeight}`;
  };
  const isSelected = () => selectedId() === props.slot.id;

  function toggleSelected() {
    const id = props.slot.id;
    setSelectedId(prev => (prev === id ? null : id));
  }

  async function onPick(e: Event) {
    const input = e.currentTarget as HTMLInputElement;
    const file = input.files?.[0];
    input.value = '';
    if (!file) return;
    const result = await replaceSlotImage(props.slot.id, file);
    if (!result.ok) {
      const reason = result.reason === 'unsupported-type' ? 'unsupported format' : 'failed to read';
      pushToast({ kind: 'error', text: `${result.filename}: ${reason}` });
    }
  }

  return (
    <div
      class="slot-row"
      data-selected={isSelected()}
      onClick={toggleSelected}
    >
      <button
        type="button"
        class="slot-thumb"
        onClick={(e) => { e.stopPropagation(); inputRef.click(); }}
        aria-label={props.slot.image ? `Replace image in slot ${props.slot.id + 1}` : `Choose image for slot ${props.slot.id + 1}`}
      >
        <Show when={props.slot.image} fallback={<div class="thumb-empty" />}>
          {img => <img src={img().objectUrl} alt="" />}
        </Show>
      </button>
      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/png,image/svg+xml"
        hidden
        onChange={onPick}
      />
      <div class="slot-info">
        <div class="slot-filename">
          {props.slot.image?.filename ?? `Slot ${props.slot.id + 1}`}
        </div>
        <div class="slot-dims">
          {dimsLabel()}
          <Show when={dpi() && !dpi()!.ok}>
            <span class="dpi-badge" title={`Recommended ≥ ${dpi()!.required}×${dpi()!.required}`}>
              ⚠ low
            </span>
          </Show>
        </div>
        <div class="slot-fit-toggle">
          <button
            type="button"
            data-fit="centre"
            disabled={!props.slot.image}
            aria-pressed={props.slot.fitTarget === 'centre'}
            onClick={(e) => { e.stopPropagation(); setFitTarget(props.slot.id, 'centre'); }}
          >
            centre
          </button>
          <button
            type="button"
            data-fit="bleed"
            disabled={!props.slot.image}
            aria-pressed={props.slot.fitTarget === 'bleed'}
            onClick={(e) => { e.stopPropagation(); setFitTarget(props.slot.id, 'bleed'); }}
          >
            bleed
          </button>
        </div>
      </div>
      <Show when={props.slot.image}>
        <button
          type="button"
          class="slot-remove"
          aria-label={`Remove image from slot ${props.slot.id + 1}`}
          onClick={(e) => { e.stopPropagation(); clearSlot(props.slot.id); }}
        >
          ×
        </button>
      </Show>
      <Show when={isSelected()}>
        <SlotRowExpanded slot={props.slot} />
      </Show>
    </div>
  );
}
